import { toast } from "react-toastify";
import { INewFile } from "../../containers/Document/Document/interfaces";

export const convertFileToBase64 = (newFile: INewFile): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        const base64String = reader.result.toString();

        const base64Data = base64String.split(",")[1];
        resolve(base64Data);
      } else {
        reject(new Error("File could not be read"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error reading file"));
    };

    reader.readAsDataURL(newFile.file);
  });
};

export const convertBase64ToBlob = (base64Data: string, contentType = ""): Blob | undefined => {
  try {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  } catch (error) {
    console.log("error: ", error);

    toast("Error parsing file");
  }
};

export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        const base64String = reader.result.toString();

        const base64Data = base64String.split(",")[1];
        resolve(base64Data);
      } else {
        reject(new Error("File could not be read"));
      }
    };

    reader.onerror = () => {
      reject(new Error("Error reading file"));
    };

    reader.readAsDataURL(blob);
  });
};

export const convertBlobToFile = (blob: Blob, name: string, type?: string): File => {
  const file = new File([blob], name, { type });
  return file;
};

export const convertBase64ToFile = async (
  base64Data: string,
  contentType = "",
  fileName = ""
): Promise<File | null> => {
  const base64toBlob = await convertBase64ToBlob(base64Data, contentType);
  if (base64toBlob) {
    const blobToFile = convertBlobToFile(base64toBlob, contentType, fileName);

    return blobToFile;
  }
  return null;
};

/**
 * Convert a base64-encoded SVG string to a PNG Blob.
 * @param {string} svgBase64 - e.g. "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0..."
 * @param {number} [scale=2] - for high-DPI output
 * @returns {Promise<Blob>}
 */
export const convertSVGBase64ToPngFile = async (svgBase64: string, scale = 2): Promise<File> => {
  const img = new Image();
  img.src = svgBase64;
  img.crossOrigin = "anonymous"; // safe for same-origin or CORS-allowed SVGs

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
  });

  const width = img.width;
  const height = img.height;

  const canvas = document.createElement("canvas");
  canvas.width = width * scale;
  canvas.height = height * scale;

  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.drawImage(img, 0, 0, width, height);
  }

  const pngBlob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Failed to convert canvas to Blob"));
      }
    }, "image/png");
  });
  const file = new File([pngBlob], "image.png", { type: "image/png" });

  return file;
};
